/**
 * Client 360's **Ledger**, **Documents** and **Chat** tabs — the last three
 * seams on this screen, filled in Wave 6C, and drawn as section cards over the
 * kit's table in Wave 15B (C14 §10.6 items 2–3; T-6.14).
 *
 * They had been rendered as dashed placeholders reading *"It arrives with Wave
 * 4C"* and *"Wave 4B"* since Wave 2A. Both waves shipped in September; the
 * panels went on saying otherwise, which is the firm's own screen telling them
 * a feature is missing that is not (Wave 5 close §3.4).
 *
 * ## Three reads, not one
 *
 * `CasePanels.tsx` takes one read for two parts because they are two halves of
 * one question. These are three questions with three different permissions
 * behind them — `ledger:can_view`, `document:can_view`, `case:can_view` — and
 * one read that needed all three would show an associate nothing on the ground
 * that they may not see the money. Each tab reads its own part when opened.
 *
 * ## A part that may not be read says so, and says it about itself
 *
 * Spec §P gives an associate no verb on `ledger` at all. The honest answer is
 * not an empty list — *"this client owes nothing"* is a different and false
 * statement — and not a broken screen either. Each part catches its own
 * refusal and reports it in one line, which is `CasePanels`' rule for
 * `due_dates: null` applied to a status code instead of a null.
 *
 * ## Nothing here re-derives a rule or a number
 *
 * The balance is the server's `GET /billing/clients/{id}/balance`, computed
 * every time (plan §8); `payment_state` is the server's word; the documents are
 * the current versions the repository decided are current. This file formats
 * and links, exactly as `lib/api/billing.ts`' own docstring requires.
 */

import { Alert, Skeleton } from '@fiveox/ui'
import { FileText, MessagesSquare, Wallet } from 'lucide-react'
import { type ReactNode, Suspense, lazy, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router'

import { type ListColumn, ListTable, RecordSection, useListTable } from '../../components/page'
import { ApiError } from '../../lib/api'
import { type Invoice, type LedgerEntry, billing, rupees } from '../../lib/api/billing'
import { type Conversation, chat } from '../../lib/api/chat'
import type { ClientProDoc } from '../../lib/api/clients'
import { type DocumentRow, documents } from '../../lib/api/documents'
import { useClientDocumentsPlace } from '../../lib/prodoc'

/** ProDoc's package, in its own chunk — only a moved firm's staff load it. */
const ProDocDocuments = lazy(() =>
  import('./ProDocDocuments').then((module) => ({ default: module.ProDocDocuments })),
)

/** How many rows a part on a summary screen shows before it stops being a
 *  summary. The module's own screen is one link away and is where a firm goes
 *  to read the whole thing. */
const RECENT = 5

const NO_ENTRIES: LedgerEntry[] = []
const NO_INVOICES: Invoice[] = []
const NO_DOCUMENTS: DocumentRow[] = []
const NO_THREADS: Conversation[] = []

/** The one-line answer for a part that could not be read.
 *
 *  `FORBIDDEN` is its own sentence because it is not a fault: this person may
 *  not see this, and telling them that is more use than "something went wrong".
 *  Everything else is the generic line, because a firm cannot act on the
 *  difference between a 500 and a dropped connection. */
export function refusal(err: unknown, what: string): string {
  if (err instanceof ApiError && err.code === 'FORBIDDEN') {
    return `You do not have permission to see ${what}.`
  }
  return `${what.charAt(0).toUpperCase()}${what.slice(1)} could not be loaded just now.`
}

function Muted({ children }: { children: ReactNode }) {
  return <p className="text-muted-foreground text-sm">{children}</p>
}

function More({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className="text-primary text-sm font-medium hover:underline">
      {children}
    </Link>
  )
}

function PartSkeleton() {
  return (
    <div role="status" aria-busy aria-label="Loading" className="space-y-2">
      <Skeleton className="h-9 w-full rounded-lg" />
      <Skeleton className="h-9 w-full rounded-lg" />
      <Skeleton className="h-9 w-2/3 rounded-lg" />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Ledger — spec FR-G3. What this client owes, and since when.
// ---------------------------------------------------------------------------

export function ClientLedgerPanel({ clientId }: { clientId: string }) {
  const [balance, setBalance] = useState<string | null>(null)
  const [advance, setAdvance] = useState<string | null>(null)
  const [entries, setEntries] = useState<LedgerEntry[] | null>(null)
  const [invoices, setInvoices] = useState<Invoice[]>(NO_INVOICES)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    void (async () => {
      try {
        // The balance and the entries are one permission (`ledger:can_view`);
        // the invoices are another (`invoice:can_view`). A firm whose associate
        // may read invoices and not the ledger sees the half they may.
        const [head, lines] = await Promise.all([
          billing.balance(clientId),
          billing.ledger(clientId, { limit: RECENT }),
        ])
        if (!live) return
        setBalance(head.data.balance)
        setAdvance(head.data.advance_held)
        setEntries(lines.data)
      } catch (err) {
        if (live) setError(refusal(err, 'this client’s ledger'))
      }
      try {
        const open = await billing.invoices({ client_id: clientId, status: 'issued' })
        if (live) setInvoices(open.data.slice(0, RECENT))
      } catch {
        // The invoice list is the *extra*. Its absence is not worth a second
        // error line on a part that has already said what it could not read.
      }
    })()
    return () => {
      live = false
    }
  }, [clientId])

  const invoiceColumns = useMemo<ListColumn<Invoice>[]>(
    () => [
      {
        key: 'number',
        label: 'Invoice',
        width: 160,
        phone: true,
        render: (invoice) => <span className="font-mono text-xs">{invoice.number ?? 'Draft'}</span>,
      },
      {
        key: 'outstanding',
        label: 'Due',
        width: 140,
        align: 'right',
        phone: true,
        render: (invoice) => (
          <span className="tabular-nums">{rupees(invoice.outstanding)} due</span>
        ),
      },
    ],
    [],
  )
  const entryColumns = useMemo<ListColumn<LedgerEntry>[]>(
    () => [
      {
        key: 'entry_date',
        label: 'Date',
        width: 110,
        phone: true,
        render: (entry) => <span className="text-muted-foreground whitespace-nowrap">{entry.entry_date}</span>,
      },
      {
        key: 'narration',
        label: 'Narration',
        width: 240,
        render: (entry) => <span className="block max-w-xs truncate sm:max-w-md">{entry.narration}</span>,
      },
      {
        key: 'amount',
        label: 'Amount',
        width: 130,
        align: 'right',
        phone: true,
        render: (entry) => <span className="tabular-nums">{rupees(entry.amount)}</span>,
      },
    ],
    [],
  )
  const invoiceList = useListTable(invoices, { columns: invoiceColumns })
  const entryList = useListTable(entries ?? NO_ENTRIES, { columns: entryColumns })

  return (
    <RecordSection
      id="client-ledger"
      icon={<Wallet size={18} aria-hidden />}
      title="Ledger"
      description="What this client owes, and the latest entries."
      actions={<More to="/billing/ledger">Ledger &amp; statement →</More>}
    >
      {error && <Muted>{error}</Muted>}
      {!error && entries === null && <PartSkeleton />}
      {!error && entries !== null && (
        <div className="space-y-5">
          <dl className="grid grid-cols-2 gap-3 sm:max-w-md">
            <div className="border-border rounded-xl border p-3">
              <dt className="text-muted-foreground text-xs">Outstanding</dt>
              <dd className="text-foreground mt-0.5 text-lg font-semibold tabular-nums">
                {rupees(balance)}
              </dd>
            </div>
            {advance !== null && advance !== '0.00' && (
              <div className="border-border rounded-xl border p-3">
                <dt className="text-muted-foreground text-xs">Advance held</dt>
                <dd className="text-foreground mt-0.5 text-lg font-semibold tabular-nums">
                  {rupees(advance)}
                </dd>
              </div>
            )}
          </dl>
          {invoices.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-foreground text-sm font-medium">Unpaid invoices</h3>
              <ListTable
                list={invoiceList}
                loading={false}
                rowKey={(invoice) => invoice.id}
                rowHref={(invoice) => `/billing/invoices/${invoice.id}`}
              />
            </div>
          )}
          {entries.length === 0 ? (
            <Muted>Nothing has been billed to this client yet.</Muted>
          ) : (
            <div className="space-y-2">
              <h3 className="text-foreground text-sm font-medium">Latest entries</h3>
              <ListTable list={entryList} loading={false} rowKey={(entry) => entry.id} />
            </div>
          )}
        </div>
      )}
    </RecordSection>
  )
}

// ---------------------------------------------------------------------------
// Documents — spec FR-B5, FR-F1. The permanent file.
// ---------------------------------------------------------------------------

/** Where a document came from and whether the client can see it. */
export function documentState(row: DocumentRow): string {
  return [
    row.version > 1 ? `v${row.version}` : null,
    // w9g, T-5.29 — **the half that works with no configuration.** A client
    // may now send a document nobody asked for (`source: 'portal'`, no
    // `request_id`); a firm that has not bound an FR-K3 rule on
    // `ca-erp.document.client_sent` would otherwise see this row as
    // indistinguishable from a file its own staff filed. A checklist answer is
    // not marked: its item moving to `received` already says where it came from.
    row.source === 'portal' && !row.request_id ? 'from the client' : null,
    // w27c — C25, ADR-0053 §5: what a visitor sent with a booking. Held until
    // staff share it; the portal does not list it (§9-90).
    row.source === 'booking' ? 'from a booking' : null,
    row.shared_at ? 'shared' : 'held',
  ]
    .filter(Boolean)
    .join(' · ')
}

/**
 * The client's documents — **ProDoc's package or ProDesk's own list**, Wave 28E
 * (T-2.33; ADR-0055 §6).
 *
 * For a firm whose documents have moved onto ProDoc (I-5's `prodoc.enabled`),
 * a staff member with a ProDoc role gets the package's library and uploader
 * (`./ProDocDocuments.tsx`, loaded only then). Everyone else gets the panel
 * below, unchanged — with the flag off that is every firm, and nothing about
 * this tab differs from before Wave 28.
 *
 * `prodoc` is the 360 read's `client.prodoc`; left `undefined`, the client read
 * I-5 names is asked instead (`useClientDocumentsPlace`).
 */
export function ClientDocumentsPanel({
  clientId,
  prodoc,
}: {
  clientId: string
  prodoc?: ClientProDoc | null
}) {
  const place = useClientDocumentsPlace(clientId, prodoc)
  if (place.kind === 'reading') return <DocumentsLoading />
  if (place.kind === 'local') return <LocalDocumentsPanel clientId={clientId} />
  return (
    <Suspense fallback={<DocumentsLoading />}>
      <ProDocDocuments
        clientId={clientId}
        apiUrl={place.apiUrl}
        folderId={place.folderId}
        fallback={(notice) => <LocalDocumentsPanel clientId={clientId} notice={notice} />}
      />
    </Suspense>
  )
}

/** The section, waiting — the same frame both branches draw into. */
function DocumentsLoading() {
  return (
    <RecordSection
      id="client-documents"
      icon={<FileText size={18} aria-hidden />}
      title="Documents"
      description="The latest in the permanent file."
    >
      <PartSkeleton />
    </RecordSection>
  )
}

/**
 * ProDesk's own list of a client's documents — the panel as it was before
 * Wave 28. `notice` is the one line a moved firm's staff member reads above it
 * when ProDoc's library is not theirs to see (`./ProDocDocuments.tsx`).
 */
export function LocalDocumentsPanel({ clientId, notice }: { clientId: string; notice?: string }) {
  const navigate = useNavigate()
  const [rows, setRows] = useState<DocumentRow[] | null>(null)
  const [total, setTotal] = useState(0)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    void documents
      .list({ client_id: clientId, limit: RECENT })
      .then((body) => {
        if (!live) return
        setRows(body.data)
        setTotal(Number(body.meta?.total ?? body.data.length))
      })
      .catch((err) => {
        if (live) setError(refusal(err, 'this client’s documents'))
      })
    return () => {
      live = false
    }
  }, [clientId])

  const columns = useMemo<ListColumn<DocumentRow>[]>(
    () => [
      {
        key: 'title',
        label: 'Document',
        width: 240,
        phone: true,
        render: (row) =>
          row.case_id ? (
            <Link
              to={`/cases/${row.case_id}/documents`}
              onClick={(event) => event.stopPropagation()}
              className="text-foreground hover:text-primary block max-w-[10rem] truncate font-medium sm:max-w-xs"
            >
              {row.title ?? row.filename}
            </Link>
          ) : (
            <span className="text-foreground block max-w-[10rem] truncate sm:max-w-xs">
              {row.title ?? row.filename}
            </span>
          ),
      },
      {
        key: 'state',
        label: 'State',
        width: 180,
        phone: true,
        // Sentence case on screen (*Held*, *From the client · shared*), as every
        // other state column is; `documentState` itself stays as it was.
        render: (row) => (
          <span className="text-muted-foreground inline-block first-letter:uppercase">{documentState(row)}</span>
        ),
      },
    ],
    [],
  )
  const list = useListTable(rows ?? NO_DOCUMENTS, { columns })

  return (
    // **No "all documents" link, because there is no such screen.** Documents
    // are read on the case they belong to (`cases/:caseId/documents`) and this
    // app has no client-level document list; a link to one would be a link to a
    // 404. Each row that has a case is its own way through.
    <RecordSection
      id="client-documents"
      icon={<FileText size={18} aria-hidden />}
      title="Documents"
      description={
        rows !== null && total > rows.length
          ? `The latest ${rows.length} of ${total} in the permanent file. One filed on a case opens there.`
          : 'The latest in the permanent file. One filed on a case opens there.'
      }
    >
      {notice && (
        <div className="mb-4">
          <Alert tone="info">{notice}</Alert>
        </div>
      )}
      {error && <Muted>{error}</Muted>}
      {!error && rows === null && <PartSkeleton />}
      {!error && rows !== null && (
        <ListTable
          list={list}
          loading={false}
          rowKey={(row) => row.id}
          onRowClick={(row) => {
            if (row.case_id) navigate(`/cases/${row.case_id}/documents`)
          }}
          empty="Nothing has been filed for this client yet."
        />
      )}
    </RecordSection>
  )
}

// ---------------------------------------------------------------------------
// Chat — spec FR-E8. The threads, staff side.
// ---------------------------------------------------------------------------

export function ClientChatPanel({ clientId }: { clientId: string }) {
  const [rows, setRows] = useState<Conversation[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    void chat
      .conversations({ client_id: clientId })
      .then((body) => {
        if (live) setRows(body.data.slice(0, RECENT))
      })
      .catch((err) => {
        if (live) setError(refusal(err, 'this client’s messages'))
      })
    return () => {
      live = false
    }
  }, [clientId])

  const columns = useMemo<ListColumn<Conversation>[]>(
    () => [
      {
        key: 'subject',
        label: 'Thread',
        width: 240,
        phone: true,
        render: (row) => (
          <span className="block max-w-[10rem] truncate sm:max-w-xs">
            {row.subject ?? (row.case_id ? 'A case thread' : 'General')}
          </span>
        ),
      },
      {
        key: 'unread',
        label: 'Unread',
        width: 90,
        align: 'right',
        render: (row) => (
          <span className={row.unread ? 'text-foreground font-medium' : 'text-muted-foreground'}>
            {row.unread || '—'}
          </span>
        ),
      },
      {
        key: 'last_message_at',
        label: 'Last message',
        width: 130,
        phone: true,
        render: (row) => (
          <span className="text-muted-foreground whitespace-nowrap">
            {row.last_message_at?.slice(0, 10) ?? '—'}
          </span>
        ),
      },
    ],
    [],
  )
  const list = useListTable(rows ?? NO_THREADS, { columns })

  return (
    <RecordSection
      id="client-chat"
      icon={<MessagesSquare size={18} aria-hidden />}
      title="Chat"
      description="The firm’s latest threads with this client."
      actions={<More to="/messages">Inbox →</More>}
    >
      {error && <Muted>{error}</Muted>}
      {!error && rows === null && <PartSkeleton />}
      {!error && rows !== null && (
        <ListTable
          list={list}
          loading={false}
          rowKey={(row) => row.id}
          rowHref={(row) => `/messages?thread=${row.id}`}
          empty="No one at the firm has a thread open with this client."
        />
      )}
    </RecordSection>
  )
}
